import type { ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import { cn } from '../lib/cn.js';

export type AuthSurface = 'customer' | 'merchant' | 'admin' | 'business';

export type AuthLayoutProps = {
  surface?: AuthSurface;
  brandName?: string;
  tagline?: string;
  /** Vertical alignment of the form column (sign-in uses center; onboarding uses start). */
  contentAlign?: 'center' | 'start';
  /** Max width of the form column within the auth shell. */
  formMaxWidth?: 'md' | 'lg' | 'xl' | '2xl';
  /** Marks onboarding surfaces for tighter density overrides in app CSS. */
  onboarding?: boolean;
  /** Optional content for a product-specific brand panel. */
  brandContent?: ReactNode;
};

const SURFACE_BRAND: Record<
  AuthSurface,
  { brandName: string; tagline: string; panelClass: string }
> = {
  customer: {
    brandName: 'BeatQ Eats',
    tagline: 'Order from local kitchens, delivered with care.',
    panelClass: 'auth-panel-customer',
  },
  merchant: {
    brandName: 'BeatQ Eats Merchant',
    tagline: 'Run service, staff, and orders from one console.',
    panelClass: 'auth-panel-merchant',
  },
  admin: {
    brandName: 'BeatQ Eats Admin',
    tagline: 'Platform operations, trust, and support tools.',
    panelClass: 'auth-panel-admin',
  },
  business: {
    brandName: 'BeatQ Eats Business',
    tagline: 'Corporate accounts, approvals, and team spend.',
    panelClass: 'auth-panel-business',
  },
};

const FORM_MAX_WIDTH: Record<NonNullable<AuthLayoutProps['formMaxWidth']>, string> = {
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
};

/** Auth shell — brand plane + form column; stacks on small screens. */
export function AuthLayout({
  surface = 'customer',
  brandName,
  tagline,
  contentAlign = 'center',
  formMaxWidth = 'md',
  onboarding = false,
  brandContent,
}: AuthLayoutProps) {
  const brand = SURFACE_BRAND[surface];
  const name = brandName ?? brand.brandName;
  const line = tagline ?? brand.tagline;

  return (
    <div
      data-layout="auth"
      data-auth-surface={surface}
      {...(onboarding ? { 'data-onboarding': 'true' } : {})}
      className={cn('auth-shell grid min-h-screen w-full grid-cols-1 bg-page md:grid-cols-2')}
    >
      <aside
        className={cn(
          'auth-brand relative flex min-h-[34vh] flex-col justify-end overflow-hidden p-lg md:min-h-screen md:justify-center',
          brand.panelClass,
        )}
      >
        <div className="auth-brand-glow pointer-events-none absolute inset-0" aria-hidden="true" />
        {brandContent ?? (
          <div className="relative z-[1] w-full max-w-md">
            <p className="auth-brand-name m-0 text-inverse">{name}</p>
            <p className="auth-brand-tagline mt-field m-0 max-w-md text-inverse/90">{line}</p>
          </div>
        )}
      </aside>
      <main
        id="main"
        tabIndex={-1}
        className={cn(
          'flex w-full min-w-0 p-lg md:px-lg',
          onboarding
            ? 'auth-onboarding-main items-start justify-center pt-md md:pt-lg'
            : contentAlign === 'start'
              ? 'items-start justify-start pt-md md:pt-lg'
              : 'items-center justify-center',
        )}
      >
        <div className={cn('auth-form-column w-full min-w-0', FORM_MAX_WIDTH[formMaxWidth])}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
